/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { RegistrationSettingsTitle } from '../Schedule/EditRegistration'
import { Flex } from '../../components/Layout'
import SuspensionSelect from '../../components/Suspensions/SuspensionSelect'
import SuspensionInput from '../../components/Suspensions/SuspensionInput'
import { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import DatePicker from '../../components/DatePicker'
import ToggleSection from '../Schedule/EditRegistration/ToggleSection'
import FormInput from '../../components/Form/FormInput'
import Icon from '../../components/Icon'
import SuspensionSubmitButton from '../../components/Suspensions/SuspensionSubmitButton'
import { useMobxStore } from '../../state'
import { useHistory, useLocation } from 'react-router-dom'
import CurrencyInput from '../../components/CurrencyInput'
import req from '@sportninja/common/api/request'

const SetupRegistration = () => {
  // LEFT COLUMN
  const [enableRegistration, setEnableRegistration] = useState(false)
  const [registrationBy, setRegistrationBy] = useState(1)
  const [maxTeamSlots, setMaxTeamSlots] = useState(0)
  const [currency, setCurrency] = useState(1)
  const [costPerRegistration, setCostPerRegistration] = useState(null)
  const [eventNumber, setEventNumber] = useState(null)
  // const [dateFormat, setDateFormat] = useState(0)
  const [dateRange, setDateRange] = useState({
    starts_at: null,
    ends_at: null,
  })

  // RIGHT COLUMN
  const [paymentCurrency, setPaymentCurrency] = useState(1)
  const [paymentMethod, setPaymentMethod] = useState(2)
  const [useDarkTheme, setUseDarkTheme] = useState(false)
  const [goLive, setGoLive] = useState(false)
  const [backgroundImage, setBackgroundImage] = useState(null)
  // const [sendPush, setSendPush] = useState(false)

  const location = useLocation()
  const history = useHistory()

  const {
    registrationTypes: {
      state: { currency_type, registration_payment_issue_type },
    },
    registration: { createRegistration },
  } = useMobxStore()

  const saveBackgroundImage = useCallback(async () => {
    try {
      let reader = new FileReader()

      reader.onloadend = async () => {
        const imageData = reader.result
        if (!imageData) {
          return
        }

        await req(
          `/schedules/${location.state?.scheduleId}/registration/image`,
          {
            method: 'POST',
            body: imageData,
          }
        )
      }

      reader.readAsDataURL(backgroundImage)
    } catch (err) {
      console.error('There was an error uploading the image', err)
    }
  }, [backgroundImage, location.state?.scheduleId])

  const onConfirmPress = useCallback(() => {
    createRegistration({
      level: 'schedule',
      scheduleId: location.state?.scheduleId,
      info: {
        online_registration_open: enableRegistration,
        official_governing_body_event_number: eventNumber,
        currency: currency,
        standard_cost: costPerRegistration,
        payment_currency: paymentCurrency,
        payment_type: paymentMethod,
        is_live: goLive,
        starts_at: dateRange.starts_at,
        ends_at: dateRange.ends_at,
        custom_link: 'SportNinja',
        dark_theme: useDarkTheme,
        push_notification: false,
        max_teams: maxTeamSlots || 0,
      },
    })
  }, [
    enableRegistration,
    maxTeamSlots,
    currency,
    costPerRegistration,
    eventNumber,
    dateRange,
    goLive,
    paymentCurrency,
    paymentMethod,
    useDarkTheme,
  ])

  if (!location.state?.scheduleId) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding-top: 10%;
        `}
      >
        <p>{"You don't have permission to access it"}</p>
        <button onClick={() => history.goBack()}>
          <p
            css={css`
              margin-top: 20px;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            Go back
          </p>
        </button>
      </div>
    )
  }

  return (
    <section
      id='registration'
      css={css`
        width: 100%;
        height: 100%;

        @media (max-width: 768px) {
          padding-bottom: 50px;
          overflow-y: auto;
        }
      `}
    >
      <div
        id='registration-body'
        css={css`
          display: flex;
          border-bottom: 1px solid ${colors.SOFT_STEEL};
          max-width: 70%;
          margin: 0 auto;
          padding: 80px 0 32px 0;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <div
          id='registration-body-left'
          css={css`
            flex: 1;
            padding-right: 20px;
          `}
        >
          <div
            css={css`
              margin-bottom: 20px;
            `}
          >
            <ToggleSection
              title='Registration Option'
              description='When turned ON, users can use the registration flow to create or include their teams for this competition.'
              isEnabled={enableRegistration}
              onClick={() => setEnableRegistration((o) => !o)}
            />
          </div>
          <RegistrationSettingsTitle>General Info</RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <SuspensionSelect
              enabledAllItemsOption={false}
              showSeparator={false}
              useFullWidth
              required
              label='Registration by'
              placeholder='-'
              value={registrationBy}
              onChange={(e) => {
                setRegistrationBy(e)
              }}
              options={[
                {
                  text: 'Team',
                  value: 1,
                },
              ]}
            />
            <SuspensionInput
              wrapCss={css`
                min-width: 178px;
              `}
              height={40}
              label='Max Number of Team Slots'
              id='team_slots'
              type='number'
              min={0}
              step={1}
              value={String(maxTeamSlots)}
              onChange={({ target }) => {
                setMaxTeamSlots(target.value)
              }}
            />
          </div>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <SuspensionSelect
              enabledAllItemsOption
              showSeparator={false}
              useFullWidth
              required
              label='Currency'
              placeholder='-'
              value={currency}
              onChange={(e) => {
                setCurrency(e)
              }}
              options={currency_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
            <div
              css={css`
                flex: 1;
              `}
            >
              <CurrencyInput
                placeholder="$0.00"
                type='text'
                onChange={({ target }) =>
                  setCostPerRegistration(target.value?.replace(/[^0-9.]/g, ''))
                }
                value={costPerRegistration}
                label='Standard Cost per Registration'
                required
              />
            </div>
          </div>
          <SuspensionInput
            height={40}
            label='Official Governing Body Event Number (Hockey Canada/US)'
            id='event_number'
            type='number'
            min={0}
            step={1}
            value={eventNumber}
            onChange={({ target }) => setEventNumber(target.value)}
          />
          <RegistrationSettingsTitle
            css={css`
              margin-top: 32px;
            `}
          >
            Registration Period
          </RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <div
              css={css`
                flex: 1;
                margin-bottom: 10px;
                min-width: 290px;
              `}
            >
              <DatePicker
                datePlaceholder={dayjs().format('YYYY-MM-DD')}
                label='Registration Start'
                hideTime
                required
                name='starts_at'
                useISOString={false}
                changeOnReset
                onChange={({ target }) => {
                  const { name, value } = target

                  setDateRange((prevDateRange) => {
                    return {
                      ...prevDateRange,
                      [name]: value,
                    }
                  })
                }}
              />
            </div>
            <div
              css={css`
                flex: 1;
                min-width: 290px;
              `}
            >
              <DatePicker
                datePlaceholder={dayjs().format('YYYY-MM-DD')}
                label='Registration End'
                hideTime
                required
                name='ends_at'
                useISOString={false}
                changeOnReset
                onChange={({ target }) => {
                  const { name, value } = target

                  setDateRange((prevDateRange) => {
                    return {
                      ...prevDateRange,
                      [name]: value,
                    }
                  })
                }}
              />
            </div>
          </div>
        </div>
        <div
          id='registration-body-right'
          css={css`
            flex: 1;
            padding-left: 20px;

            @media (max-width: 768px) {
              padding-left: 0px;
              margin-top: 20px;
            }
          `}
        >
          <RegistrationSettingsTitle>
            Payments From SportNinja
          </RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              width: 100%;
              justify-content: space-between;

              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <SuspensionSelect
              useFullWidth
              enabledAllItemsOption={false}
              showSeparator={false}
              required
              label='Currency'
              placeholder='-'
              value={paymentCurrency}
              onChange={(e) => {
                setPaymentCurrency(e)
              }}
              options={currency_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
            <div
              css={css`
                width: 16px;
              `}
            />
            <SuspensionSelect
              useFullWidth
              enabledAllItemsOption={false}
              showSeparator={false}
              required
              label='Issue payment as'
              placeholder='-'
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e)
              }}
              options={registration_payment_issue_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
          </div>
          <RegistrationSettingsTitle
            css={css`
              margin-top: 20px;
            `}
          >
            Extra Options
          </RegistrationSettingsTitle>
          <Flex
            noFlex
            alignItems='center'
            justifyContent='space-between'
            css={css`
              padding: 8px 0px;
              height: 40px;
              font-weight: 500;
              font-size: 16px;
              line-height: 16px;
              margin: 16px 0;
              transition: border-color 0.15s ease-in-out;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            <div
              css={css`
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding-right: 8px;
              `}
            >
              <span>A unique link will be generated for this registration</span>
            </div>
          </Flex>
          <div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                width: 100%;
                margin-right: 16px;
                flex: 1;

                input {
                  display: inline;
                  appearance: none;
                  opacity: 0;
                  width: 1px;
                }
              `}
            >
              <span
                css={css`
                  font-weight: 500;
                  font-size: 14px;
                  margin-bottom: 8px;
                `}
              >
                Custom background
              </span>
              <Flex>
                <label
                  htmlFor='background-image'
                  css={css`
                    display: flex;
                    flex: 1;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 16px;
                    height: 40px;
                    background: ${colors.HEADER};
                    border: 1px solid ${colors.SOFT_STEEL};
                    border-radius: 4px;
                    cursor: pointer;

                    transition: border-color 0.15s ease-in-out;

                    i {
                      transition: color 0.15s ease-in-out;
                    }

                    &:hover {
                      border-color: white;

                      i {
                        color: white;
                      }
                    }
                  `}
                >
                  {backgroundImage?.name || 'Select an image'}
                  <Icon
                    name='file-upload'
                    color={colors.DEFAULT_FLAIR}
                    fontSize={16}
                  />
                </label>

                <input
                  type='file'
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setBackgroundImage(e.target.files[0])
                    }
                  }}
                  id='background-image'
                />
              </Flex>
            </div>
            <div
              css={css`
                width: 100%;
                margin-top: 35px;
              `}
            >
              <ToggleSection
                title='Dark Theme'
                description='A dark theme will set Dark background and text in white.'
                isEnabled={useDarkTheme}
                onClick={() => setUseDarkTheme((o) => !o)}
              />
            </div>
          </div>
          <div
            css={css`
              margin-top: 35px;
            `}
          >
            <ToggleSection
              title='Go Live'
              description={
                goLive
                  ? 'Your registration will become available immediately.'
                  : dateRange?.starts_at && dateRange?.ends_at
                  ? `Your registration will become available for use during the period of ${dayjs(
                      dateRange?.starts_at
                    )?.format('MMMM DD, YYYY')}, to ${dayjs(
                      dateRange?.ends_at
                    )?.format('MMMM DD, YYYY')}`
                  : 'You can go live when you are ready to accept registrations.'
              }
              isEnabled={goLive}
              onClick={() => setGoLive((o) => !o)}
            />
          </div>
          {/* <div
            css={css`
              margin-top: 35px;
            `}
          >
            <ToggleSection
              title='Send push notification to all players'
              description='We will send a notification to all players with any updates to registration status.'
              isEnabled={sendPush}
              onClick={() => setSendPush((o) => !o)}
            />
          </div> */}
        </div>
      </div>
      <div
        id='registration-footer'
        css={css`
          margin-top: 32px;
        `}
      >
        <Flex alignItems='center' justifyContent='center'>
          <SuspensionSubmitButton onClick={() => history.goBack()}>
            Cancel
          </SuspensionSubmitButton>
          <SuspensionSubmitButton
            disabled={
              !costPerRegistration || !dateRange.starts_at || !dateRange.ends_at
            }
            onClick={async () => {
              onConfirmPress()
              await saveBackgroundImage()
              history.goBack()
            }}
            isSolid
            css={css`
              margin-left: 40px;
            `}
          >
            Confirm
          </SuspensionSubmitButton>
        </Flex>
      </div>
    </section>
  )
}

export default SetupRegistration
